import React, { useEffect, useRef, useState, type FC, useContext } from 'react';
import ReactGA from 'react-ga4';
import moment from 'moment';
import Header from '../../components/header/header';
import TabMenu from '../../components/tabMenu/tabMenu';
import DetailLoadingShell from './components/detailLoadingShell/detailLoadingShell';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ObrasAPI from '../../services/APIObras';
import { useTranslation } from 'react-i18next';
import GeneralData from './generalData/generalData';
import './fullDetail.scss';
import DescriptionPhoto from './descriptionPhoto/descriptionPhoto';
import Dates from './dates/dates';
import Efficiency from './efficiency/efficiency';
import HumanTeam from './humanTeam/humanTeam';
import LocationEnvironment from './localizationEnvironment/localizationEnvironment';
import { DetailsViewContext } from '../../context/DetailsViewProvider';
import { UserModeContext } from '../../context/UserModeProvider';
import Budget from './budget/budget';
import Contract from './contract/contract';
import Partners from './partners/partners';
import TechnicalAspects from './technicalAspects/technicalAspects';
import ErrorDetails from './components/ErrorDetails/ErrorDetails';
import Clients from './clients/clients';
import Classification from './classification/classification';
import Bim from './bim/bim';
import { AuthContext } from '../../services/authContextProvider';
import { SaveContext } from '../../context/SaveProvider';
import draftValidation, { draftValidationFinal } from '../../validations/draft';
import ESG from './esg/esg';
import { ValidationError } from 'yup';
import { ObraProperties, WandaProject } from '../../types/ObraDetails';

type ProjectValidationError = ValidationError & {
  inner: ProjectValidationError[];
  message: {
    optional?: boolean;
    text?: string;
    textField?: string;
  };
  path: string;
};

interface ValidationErrorInfo {
  field: string;
  text?: string;
  textField?: string;
  index: string | number;
  fieldField?: string;
}

type ValidationErrorsByTab = Record<string, ValidationErrorInfo[]>;

interface ParsedOption {
  tabLabel: string;
  title: string;
  component: JSX.Element;
  error: false | ValidationErrorInfo[];
}

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) return parsedValue;
  }

  return fallback;
};

const toOptionalNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) return parsedValue;
  }

  return undefined;
};

const buildObraShellFromWandaProject = (project: WandaProject, fallbackCode: string): ObraProperties => ({
  aspectosTecnicos: {
    procesosConstructivos: [],
    unidades: [],
    tiposObras: [],
  },
  cliente: {
    asistenciasTecnicas: [],
    direccionObra: [],
    disenador: [],
    codCliente: '',
    nomCliente: '',
  },
  contrato: {
    alcance: [],
    fuentesFinanciacion: [],
    periodoGarantia: '',
    tipoPeriodoGarantia: '',
    modelo: [],
    pago: [],
    tipos: [],
    codEstado: project.status || '',
    nomEstado: '',
  },
  descripcion: {
    descDetallada: {},
    infoDestacable: {},
    descGeneral: {},
    documentacionFaseLicitacion: '',
    documentacionProyecto: '',
    fotos: [],
    webs: [],
  },
  fechasPlazos: {
    fecActaRecepcion: project.dates?.receptionCertificateDate || '',
    fecActaReplanteo: project.dates?.replanningDate || '',
    fecFinEjecucion: '',
    fecFinalizacionActualizada: project.dates?.updatedExpectedEndDate || '',
    fecFinalizacionInicial: project.dates?.expectedEndDate || '',
    fecFirmaContrato: project.dates?.contractSignatureDate || '',
    fecInicio: project.startDate || '',
    plazoActualizado: project.dates?.updatedProjectTermInMonths?.toString() || '',
    plazoInicial: project.dates?.initialProjectTermInMonths || 0,
    tipoPlazoInicial: '',
  },
  general: {
    codCentro: project.code || fallbackCode,
    nomOficialCentro: project.officialName || '',
    nomPais: project.countryName || '',
    codLicitacion: toNumber(project.tenderCode),
    actividadNegocio: project.companyName || project.companyId || '',
    codPais: project.countryId || '',
    codSupercentro: toNumber(project.departmentId),
    codUne: '',
    codZona: toNumber(project.zoneId),
    nomInternoCentro: project.name || project.officialName || '',
    nomResponsable: project.responsible || '',
    nomSupercentro: '',
    nomUne: '',
    nomZona: '',
    nomEstadoPhia: '',
    codEstadoPhia: 1,
    porcParticipacionActualizada: 0,
  },
  localizacion: {
    coordenadas: {
      lat: toOptionalNumber(project.location?.latitude),
      lon: toOptionalNumber(project.location?.longitude),
    },
    espacioProteccion: [],
    especiesPeligro: [],
    zonasProteccion: [],
    kmz: '',
    municipioPrincipal: {
      nomMunicipio: '',
      codigoPostal: project.location?.zipcode || '',
    },
    municipiosAdicionales: [],
  },
  presupuesto: {
    fecAdjudicacion: project.dates?.awardDate || '',
    fecAprobacionEconomica: '',
    fecAprobacionEconomicaFinal: '',
    monedaLocal: '',
    importeAdjudicacion: {
      monedaLocal: 0,
      monedaEur: project.economicData?.contractAwardAmountEur || 0,
      accionaMonedaLocal: 0,
      accionaMonedaEur: 0,
      tipoCambio: 0,
    },
    importeActualizado: {
      monedaLocal: 0,
      monedaEur: 0,
      accionaMonedaLocal: 0,
      accionaMonedaEur: 0,
      tipoCambio: 0,
    },
    importeActualizadoFinal: {
      monedaLocal: 0,
      monedaEur: 0,
      accionaMonedaLocal: 0,
      accionaMonedaEur: 0,
      tipoCambio: 0,
    },
  },
  socios: {
    empresas: [],
    empresasActualizadas: [],
    fechaParticipacionActualizacion: '',
    fechaParticipacionAntigua: '',
  },
  clasificacion: {
    grupos: [],
  },
  bim: {
    requisitos: 0,
    notaGlobal: 0,
    notaUsosBIM: 0,
    notaUsosGestion: 0,
    notaUsosEstandarizacion: 0,
    categorizacion: {
      codigo: 0,
      descripcion: '',
    },
    madurez: {
      codigo: 0,
      descripcion: '',
    },
    usosBIM: [],
    cde: false,
    bimProyecto: false,
    fecModif: '',
  },
  esg: {
    salvaguardasSociales: [],
    certificadosSostenibilidad: [],
  },
  taxonomics: {
    activities: [],
    eligibility: false,
  },
});

export const tabMap = {
  'general': 0,
  'descripcion': 1,
  'localizacion': 2,
  'presupuesto': 3,
  'fechas': 4,
  'contrato': 5,
  'cliente': 6,
  'socios': 7,
  'aspectos-tecnicos': 8,
  'esg': 9,
  'bim': 10,
  'clasificacion': 11,
  'rendimientos': 12,
  'equipo-humano': 13,
};

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const FullDetail: FC = () => {
  const { codCentro } = useParams<string>();
  const location = useLocation();
  const [t, i18n] = useTranslation();
  const userModeContext = useContext(UserModeContext);
  const {
    obra,
    setObra,
    wandaProject,
    wandaProjectLoading,
    wandaProjectError,
    clearLoadedDetailSections,
  } = useContext(DetailsViewContext);
  const projectId = wandaProject?.id;
  const currentTab = location.pathname.split('/')[3];
  const initialTab = tabMap[currentTab as keyof typeof tabMap] || 0;
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [, setMapLoaded] = useState(false);
  const [activateErrors, setActivateErrors] = useState(false);
  const [numberError, setNumberError] = useState(0);
  const authContext = useContext(AuthContext);
  const defaultMode = authContext.defaultMode();
  const [changeMode, setUserMode] = useState(userModeContext.userMode?.toString() || defaultMode);
  const [loadError, setLoadError] = useState<ObraProperties>();
  const saveContext = useContext(SaveContext);
  const navigate = useNavigate();
  const detailVersion = changeMode === 'editor' || changeMode === 'editorDisabled' ? 'editor' : 'viewer';
  const pendingPhiaStatus = useRef<{ nomEstadoPhia?: string; codEstadoPhia?: number }>();

  useEffect(() => () => authContext.clearProjectCapabilities(), [authContext.clearProjectCapabilities]);

  useEffect(() => {
    if (!wandaProject || !codCentro) return;

    setLoadError(undefined);
    const shell = buildObraShellFromWandaProject(wandaProject, codCentro);
    if (pendingPhiaStatus.current) {
      shell.general.nomEstadoPhia = pendingPhiaStatus.current.nomEstadoPhia || '';
      shell.general.codEstadoPhia = pendingPhiaStatus.current.codEstadoPhia ?? shell.general.codEstadoPhia;
    }
    setObra(shell);
  }, [codCentro, wandaProject, setObra]);

  useEffect(() => {
    if (!wandaProjectError) return;

    setLoadError(buildObraShellFromWandaProject({ id: '', code: codCentro || '' }, codCentro || ''));
  }, [codCentro, wandaProjectError]);

  useEffect(() => {
    let active = true;
    if (!codCentro) return () => {
      active = false;
    };

    pendingPhiaStatus.current = undefined;
    const api = ObrasAPI.getInstance();
    api.getPhiaProjectStatusAPI(codCentro, detailVersion)
      .then((phiaStatus) => {
        if (!active || !phiaStatus) return;

        pendingPhiaStatus.current = phiaStatus;
        setObra((currentObra) => currentObra ? {
          ...currentObra,
          general: {
            ...currentObra.general,
            nomEstadoPhia: phiaStatus.nomEstadoPhia || '',
            codEstadoPhia: phiaStatus.codEstadoPhia ?? currentObra.general.codEstadoPhia,
          },
        } : currentObra);
      })
      .catch(() => {
        // The basic WANDA detail remains available if the PHIA status is delayed.
      });

    return () => {
      active = false;
    };
  }, [codCentro, detailVersion, i18n.language, setObra]);

  useEffect(() => {
    let active = true;
    if (!codCentro) return () => {
      active = false;
    };

    const api = ObrasAPI.getInstance();
    api.getProjectCapabilities(codCentro)
      .then((capabilities) => {
        if (active) authContext.setProjectCapabilities(capabilities);
      })
      .catch(() => {
        // Capabilities should not block the basic project detail.
      });

    return () => {
      active = false;
    };
  }, [authContext.setProjectCapabilities, codCentro]);

  /**
   * Only show errors when it's edition mode and button send to validate it's clicked
   * or one month left for end of the project and the status is not validated final
   * @returns true, show errors in the components
   */
  const hasToShowErrors = () => {
    const isFinalValidated = obra?.general?.codEstadoPhia === 8;
    return userModeContext.userMode === 'editor' && (saveContext.isValidation || (!isFinalValidated && oneMonthLeft()));
  };

  /**
   * Check if one month left for the end of the project
   * @returns
   */
  const oneMonthLeft = () => {
    let isOnTime = false;
    if (obra) {
      const finishTime = obra.fechasPlazos.fecFinalizacionActualizada ?
        moment(obra.fechasPlazos.fecFinalizacionActualizada, 'DD/MM/YYYY') :
        moment(obra.fechasPlazos.fecFinalizacionInicial, 'DD/MM/YYYY');
      isOnTime = moment().isAfter(finishTime.subtract(30, 'days'));
    }
    return isOnTime;
  };

  const options = [
    { tabLabel: '', title: t('details.generalData.tab'), component: <GeneralData codCentro={codCentro!} wandaProject={wandaProject} wandaProjectLoading={wandaProjectLoading} wandaProjectError={wandaProjectError} /> },
    {
      tabLabel: 'descripcion',
      title: t('details.description.tab'),
      component: <DescriptionPhoto
        obra={obra!}
        codCentro={codCentro!}
        wandaProject={wandaProject}
        wandaProjectLoading={wandaProjectLoading}
        wandaProjectError={wandaProjectError}
        version={changeMode === 'editor' || changeMode === 'editorDisabled' ? 'editor' : 'viewer'}
      />,
    },
    {
      tabLabel: 'localizacion',
      title: t('details.location.tab'),
      component: <LocationEnvironment
        obra={obra!}
        codCentro={codCentro!}
        wandaProject={wandaProject}
        wandaProjectLoading={wandaProjectLoading}
        wandaProjectError={wandaProjectError}
        version={detailVersion}
      />,
    },
    { tabLabel: 'presupuesto', title: t('details.budget.tab'), component: <Budget codCentro={codCentro!} wandaProject={wandaProject} wandaProjectLoading={wandaProjectLoading} wandaProjectError={wandaProjectError} /> },
    {
      tabLabel: 'fechasPlazos',
      title: t('details.dates.tab'),
      component: <Dates
        obra={obra!}
        codCentro={codCentro!}
        wandaProject={wandaProject}
        wandaProjectLoading={wandaProjectLoading}
        wandaProjectError={wandaProjectError}
        version={detailVersion}
      />,
    },
    { tabLabel: 'contrato', title: t('details.contract.tab'), component: <Contract codCentro={codCentro!} wandaProject={wandaProject} wandaProjectLoading={wandaProjectLoading} wandaProjectError={wandaProjectError} /> },
    {
      tabLabel: 'cliente',
      title: t('details.clients.tab'),
      component: <Clients
        obra={obra!}
        codCentro={codCentro!}
        wandaProject={wandaProject}
        wandaProjectLoading={wandaProjectLoading}
        wandaProjectError={wandaProjectError}
        version={detailVersion}
      />,
    },
    { tabLabel: 'socios', title: t('details.partners.tab'), component: <Partners codCentro={codCentro!} projectId={projectId} projectIdLoading={wandaProjectLoading} projectIdError={wandaProjectError} /> },
    {
      tabLabel: 'aspectosTecnicos',
      title: t('details.technical.tab'),
      component: <TechnicalAspects
        obra={obra!}
        codCentro={codCentro!}
        wandaProject={wandaProject}
        wandaProjectLoading={wandaProjectLoading}
        wandaProjectError={wandaProjectError}
        userMode={changeMode || 'viewer'}
        version={detailVersion}
      />,
    },
    {
      tabLabel: 'esg',
      title: t('details.esg.tab'),
      component: <ESG
        obra={obra!}
        codCentro={codCentro!}
        wandaProject={wandaProject}
        wandaProjectLoading={wandaProjectLoading}
        wandaProjectError={wandaProjectError}
        version={changeMode === 'editor' || changeMode === 'editorDisabled' ? 'editor' : 'viewer'}
      />,
    },
    { tabLabel: 'bim', title: t('details.bim.tab'), component: <Bim codCentro={codCentro!} projectId={projectId} projectIdLoading={wandaProjectLoading} projectIdError={wandaProjectError} /> },
    { tabLabel: 'clasificacion', title: t('details.classification.tab'), component: <Classification codCentro={codCentro!} projectId={projectId} projectIdLoading={wandaProjectLoading} projectIdError={wandaProjectError} /> },
    { tabLabel: '', title: t('details.efficiency.tab'), component: <Efficiency /> },
    { tabLabel: '', title: t('details.humanTeam.tab'), component: <HumanTeam /> },
  ];
  useEffect(() => {
    clearLoadedDetailSections();
    saveContext.handleValidationClicked(false);
    setTitles(options.map((o) => (o.title)));
    setParsedOptions(options.map((o) => ({ ...o, error: false })));
  }, [codCentro, detailVersion, i18n.language, saveContext.reloadData]);

  useEffect(() => {
    setActivateErrors(hasToShowErrors());
  }, [saveContext.isValidation, obra]);

  useEffect(() => {
    if (activateErrors) {
      checkError();
    } else {
      setNumberError(0);
    }
  }, [activateErrors]);

  const changeModeUser = (value: string) => {
    setUserMode(value);
  };

  useEffect(() => {
    const tab = location.pathname.split('/')[3];
    const tabIndex = tabMap[tab as keyof typeof tabMap] || 0;
    setSelectedTab(tabIndex);
  }, [location.pathname]);


  const [parsedOptions, setParsedOptions] = useState<ParsedOption[]>(options.map((o) => ({ ...o, error: false })));
  const [titles, setTitles] = useState(options.map((o) => (o.title)));
  const handleClickTab = (tabNumber: number) => {
    if (tabNumber === 2) {
      setMapLoaded(true);
    }
    setSelectedTab(tabNumber);
    const tab = Object.keys(tabMap).find(key => tabMap[key as keyof typeof tabMap] === tabNumber) as keyof typeof tabMap;
    ReactGA.event('click_tab', {
      event_category: tab,
      capability: 'can_read',
      code: obra?.general?.codCentro
    });
    navigate(`/${codCentro}/detalles/${tab}`);

  };

  const checkError = async () => {
    const unitsOfProcess = obra?.aspectosTecnicos.procesosConstructivos.filter((p) => p.unidades.length > 0).length;
    const hasProcessWithUnits = unitsOfProcess && unitsOfProcess > 0 ? true : false; // If some process has one or more related units, we modify the validation of the units
    const validationPromises = [draftValidation(t, hasProcessWithUnits).validate(obra, { abortEarly: false })];
    const results = await Promise.all(validationPromises.map((p) => p.catch((e: unknown) => e)));
    let validationErrors = results.filter((result): result is ProjectValidationError => result instanceof ValidationError);
    let currentNumberError = 0;

    if (oneMonthLeft()) {
      const validationPromisesFinal = [draftValidationFinal(t).validate(obra, { abortEarly: false })];
      const resultsFinal = await Promise.all(validationPromisesFinal.map((p) => p.catch((e: unknown) => e)));
      const validationErrorsFinal =
        resultsFinal.filter((result): result is ProjectValidationError => result instanceof ValidationError);
      validationErrors = validationErrors.concat(validationErrorsFinal);
    }

    if (validationErrors.length) {
      const requiredErrorsTabs: ValidationErrorsByTab = {};

      for (const v of validationErrors) {
        for (const error of v.inner) {
          if (!error.message.optional) {
            currentNumberError++;
            const data = error.path.split('.');
            if (!requiredErrorsTabs[data[0]]) {
              requiredErrorsTabs[data[0]] = [];
            }
            const dataSplit = data[1].split('[');
            let fieldField: string | undefined, index: string | number = 0;
            if (dataSplit.length > 1 && data.length > 2) {
              const endSplit = dataSplit[1].split(']');
              index = endSplit[0];
              fieldField = data[data.length - 1];
              if (fieldField === 'nombre') fieldField = data[data.length - 2];
            }
            requiredErrorsTabs[data[0]].push({
              field: dataSplit[0],
              text: error.message.text,
              textField: error.message.textField,
              index,
              fieldField
            });
          }
        }
      }
      if (numberError !== currentNumberError) {
        setNumberError(currentNumberError);
        handleFinalErrors(requiredErrorsTabs);
      }
    }
  };

  const renderTab = () => {

    if (activateErrors) {
      checkError();
    }

    const renderComponent = () => {
      if (activateErrors && parsedOptions[selectedTab].error) {
        return React.cloneElement(
          options[selectedTab].component,
          { errors: parsedOptions[selectedTab].error }
        );
      }
      return options[selectedTab].component;
    };
    return <>
      {renderComponent()}
    </>;
  };

  const handleFinalErrors = (errors: ValidationErrorsByTab | ValidationErrorInfo[]) => {
    const errorsByTab: ValidationErrorsByTab = Array.isArray(errors) ? {} : errors;
    const opts = parsedOptions.map((opt) => {
      const tab = opt.tabLabel;
      if (tab && Object.keys(errorsByTab).includes(tab) && activateErrors) {
        return { ...opt, error: errorsByTab[tab] };
      }
      return { ...opt, error: false as const };
    });
    setParsedOptions(opts);
  };

  return (
    <>
      {!obra && !loadError ?
        <DetailLoadingShell codCentro={codCentro} /> :
        <div data-testid="home-test" className='home'>

          <div>
            <Header>
              <TabMenu
                obra={obra || loadError!}
                setUserMode={changeModeUser}
                options={parsedOptions}
                titles={titles}
                selectedTab={selectedTab}
                handleClickTab={handleClickTab}
                handleFinalErrors={handleFinalErrors}
              />
            </Header>
            {obra &&
                    <>
                      <div className={`contentFullDetail ${obra.general.fechaUltimaValidacion ? 'dateLastValidation' : ''}`}>
                        {renderTab()}
                      </div>
                    </>
            }
            {loadError &&
                    <ErrorDetails />
            }
          </div>
        </div>
      }
    </>
  );
};

export default FullDetail;
